import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronRight, X, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { ONBOARDING_STEPS, isOnboardingStepDone } from '../config/onboardingSteps';
import { useOnboardingNavRefresh } from '../context/OnboardingNavRefreshContext';

export function OnboardingPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { bump } = useOnboardingNavRefresh();
  const { profile, activeMarinaId } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<Record<string, boolean>>({});
  const [detected, setDetected] = useState<Record<string, boolean>>({});
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const needsMarinaSelection = activeMarinaId === null;

  const load = useCallback(() => {
    if (needsMarinaSelection) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .getOnboarding()
      .then((data) => {
        setProgress(data.onboarding_progress ?? {});
        setDetected(data.detected ?? {});
        setCanEdit(data.can_edit);
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          toast.error(err.message);
        }
        setProgress({});
        setDetected({});
        setCanEdit(false);
      })
      .finally(() => {
        setLoading(false);
        bump();
      });
  }, [needsMarinaSelection, toast, bump]);

  useEffect(() => {
    load();
  }, [load]);

  function stepDone(stepId: string): boolean {
    return isOnboardingStepDone(stepId, detected, progress);
  }

  async function toggleStep(stepId: string) {
    if (!canEdit || detected[stepId]) return;
    const previous = { ...progress };
    const newProgress = { ...previous, [stepId]: !previous[stepId] };
    setProgress(newProgress);
    setSaving(stepId);
    try {
      await api.updateOrg({ onboarding_progress: newProgress });
      bump();
    } catch (err) {
      setProgress(previous);
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error(t('errors.somethingWentWrong'));
      }
    } finally {
      setSaving(null);
    }
  }

  const completedCount = ONBOARDING_STEPS.filter((s) => stepDone(s.id)).length;
  const requiredSteps = ONBOARDING_STEPS.filter((s) => !s.optional);
  const allRequiredDone = requiredSteps.every((s) => stepDone(s.id));
  const progressPct = Math.round((completedCount / ONBOARDING_STEPS.length) * 100);

  if (loading && !needsMarinaSelection) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (needsMarinaSelection) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-center">
          <h1 className="text-lg font-semibold text-gray-900">{t('onboarding.pickMarinaTitle')}</h1>
          <p className="text-sm text-gray-600 mt-2">{t('onboarding.pickMarinaBody')}</p>
          <button
            type="button"
            onClick={() => navigate('/org')}
            className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            {t('onboarding.goToMarinas')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('onboarding.welcome', { name: profile?.name ? `, ${profile.name.split(' ')[0]}` : '' })}
            </h1>
            <p className="text-gray-500 mt-1">{t('onboarding.subtitle')}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-gray-600 p-1"
            title={t('onboarding.skipSetup')}
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!canEdit && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t('onboarding.readOnlyHint')}
          </div>
        )}

        {canEdit && <p className="mb-6 text-sm text-gray-600">{t('onboarding.howItWorks')}</p>}

        {/* Progress bar */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              {t('onboarding.stepsCompleted', { done: completedCount, total: ONBOARDING_STEPS.length })}
            </span>
            <span className="text-sm font-semibold text-blue-600">{progressPct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {allRequiredDone && (
            <p className="text-sm text-green-700 font-medium mt-3">
              ✓ {t('onboarding.allDone')}
            </p>
          )}
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {ONBOARDING_STEPS.map((step) => {
            const done = stepDone(step.id);
            const fromData = !!detected[step.id];
            const interactive = canEdit && !fromData;
            return (
              <div
                key={step.id}
                className={`bg-white rounded-xl border p-4 shadow-sm transition-all ${
                  done ? 'border-green-200 bg-green-50/50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => interactive && toggleStep(step.id)}
                    disabled={!interactive || saving === step.id}
                    title={!canEdit ? t('onboarding.orgAdminOnly') : fromData ? t('onboarding.autoHint') : undefined}
                    className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      done
                        ? 'bg-green-600 border-green-600'
                        : 'border-gray-300 hover:border-blue-500'
                    } ${interactive ? 'cursor-pointer' : 'cursor-default opacity-90'} ${
                      saving === step.id ? 'opacity-50' : ''
                    } ${!interactive && !done ? 'opacity-40' : ''}`}
                  >
                    {done && <Check className="w-3 h-3 text-white" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`text-sm font-medium ${done ? 'text-gray-700' : 'text-gray-900'}`}
                      >
                        {t(`onboarding.steps.${step.id}.title`)}
                      </span>
                      {step.optional && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                          {t('onboarding.optional')}
                        </span>
                      )}
                      {fromData && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">
                          <Sparkles className="w-3 h-3" />
                          {t('onboarding.fromSettings')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t(`onboarding.steps.${step.id}.description`)}
                    </p>
                  </div>

                  <Link
                    to={step.link}
                    className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {t(`onboarding.steps.${step.id}.linkLabel`)}
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
            type="button"
          >
            {t('onboarding.skipSetup')}
          </button>
        </div>
      </div>
    </div>
  );
}
